import React, { FC, ReactNode, useLayoutEffect, useRef, useState } from "react";
import styles from "./card-krookie.module.scss";
import logo from "../../../images/logo-80.svg";
import QrCode from "../../qr-code/qr-code";
import { CardKrookies, TableRow } from "../../../interfaces/krookie";
import compass from "../../../images/compass-north-white.svg";
import i18n from "../../../i18n";

interface CardKrookieProps {
  data: CardKrookies;
  // On-screen editing preview only shows page 1, even in two-page mode —
  // page 2 (and the PDF's actual multi-page output) only gets built when
  // the user prints/saves, from a separate, undisplayed render of this
  // same component without this flag (see document-krookie.tsx).
  previewOnly?: boolean;
}

// The printed/exported Krookie document is always Arabic, regardless of
// which language the platform UI is currently set to — a fixed translator
// (not useTranslation()'s t, which follows the active UI language) plus an
// explicit dir="rtl" below so the document's own RTL layout never depends
// on the surrounding app's direction.
const t = i18n.getFixedT("ar");

type TableDensity = "normal" | "compact" | "dense" | "denser";

// More coordinates than this and the table no longer shrinks to fit one
// page — it spills onto a second page instead (full-size type, every row
// kept), taking the notes box and footer with it. Below this, the
// existing single-page shrink-to-fit behaviour (see the density effects
// below) still applies.
const SPLIT_ROW_THRESHOLD = 24;
// tbody td: line-height 12px + padding-block 8px top/bottom — fixed by
// CSS regardless of content, so page 1's row budget can be computed
// directly from one measurement instead of a slow per-row search.
const TABLE_ROW_HEIGHT = 28;
const PAGE_HEIGHT = 842;

const densityClassName = (density: TableDensity) =>
  [
    styles.tableWrapper,
    density === "compact" && styles.compact,
    density === "dense" && styles.dense,
    density === "denser" && styles.denser,
  ]
    .filter(Boolean)
    .join(" ");

const CoordinatesTable: FC<{
  rows: TableRow[];
  plotNumber: string;
  wgs: string;
  area: string | number;
  density: TableDensity;
  tableRef?: React.Ref<HTMLDivElement>;
}> = ({ rows, plotNumber, wgs, area, density, tableRef }) => (
  <div className={densityClassName(density)} ref={tableRef}>
    <div className={styles.tableTitle}>
      <span>
        {t("card-krookie.plotNumber")} {plotNumber} ({t("card-krookie.system")}{" "}
        {wgs})
      </span>
    </div>
    <table className={styles.table}>
      <thead>
        <tr>
          <th className={styles.headCell}>{t("card-krookie.line")}</th>
          <th className={styles.headCell}>{t("card-krookie.north")}</th>
          <th className={styles.headCell}>{t("card-krookie.east")}</th>
          <th className={styles.headCell}>{t("card-krookie.distance")}</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row, i) => (
          <tr key={i} className={styles.row}>
            <td className={styles.cell}>{row.line}</td>
            <td className={styles.cell}>
              {row.north != null ? Number(row.north).toFixed(2) : ""}
            </td>
            <td className={styles.cell}>
              {row.east != null ? Number(row.east).toFixed(2) : ""}
            </td>
            <td className={styles.cell}>
              {row.distance ? Math.round(Number(row.distance)) : ""}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
    <div className={styles.tableFooter}>
      <span>
        {t("card-krookie.areaTable")} = {Math.round(Number(area))}
      </span>
    </div>
  </div>
);

const NotesBox: FC<{
  filteredNotes: string[];
  hideNoNotesText: boolean;
  notesRef?: React.Ref<HTMLDivElement>;
}> = ({ filteredNotes, hideNoNotesText, notesRef }) => (
  <div className={styles.notes} ref={notesRef}>
    <span className={styles.titleNotes}>{t("card-krookie.notes")}:</span>
    {filteredNotes.length > 0 ? (
      <ul className={styles.list}>
        {filteredNotes.map((note, index) => (
          <li key={index}>
            {index + 1}.{note}
          </li>
        ))}
      </ul>
    ) : !hideNoNotesText ? (
      <p className={styles.noNotes}>{t("card-krookie.noNotes")}</p>
    ) : null}
  </div>
);

const FooterAndDisclaimer: FC<{ data: CardKrookies }> = ({ data }) => (
  <>
    <div className={styles.footer}>
      <div className={styles.documentInfoWrapper}>
        <div className={styles.documentInfo}>
          <div className={styles.infoBlock}>
            <span>
              {t("card-krookie.previousKrookie")}&nbsp;
              <span className={styles.symbolNumber}>№</span>:
            </span>
            <p>{data.previousKrookieCode}</p>
          </div>
          <div className={styles.infoBlock}>
            <span>
              {t("card-krookie.oldPlot")}&nbsp;
              <span className={styles.symbolNumber}>№</span>:
            </span>
            <p>{data.oldPlotNumber}</p>
          </div>
        </div>
        <div className={styles.documentInfo}>
          <div className={styles.infoBlock}>
            <span>{t("card-krookie.drafter")}:</span>
            <p>{data.drafterName}</p>
          </div>
          <div className={styles.infoBlock}>
            <span>{t("card-krookie.approver")}:</span>
            <p>{data.directorName}</p>
          </div>
        </div>
        <div className={styles.documentInfo}>
          <div className={styles.infoBlock}>
            <span>{t("card-krookie.signature")}:</span>
            <p>
              {t("card-krookie.date")}: {data.signatureDrafter}
            </p>
          </div>
          <div className={styles.infoBlock}>
            <span>{t("card-krookie.signature")}:</span>
            <p>
              {t("card-krookie.date")}: {data.signatureDirector}
            </p>
          </div>
        </div>
        <div className={styles.documentInfo}>
          <p className={styles.stamp}>{t("card-krookie.stamp")}</p>
        </div>
      </div>
    </div>
    <p className={styles.disclaimer}>{t("card-krookie.disclaimer")}</p>
  </>
);

const CardKrookie: FC<CardKrookieProps> = ({ data, previewOnly = false }) => {
  const filteredNotes = data.notes.filter((note) => note.trim() !== "");
  // Top of the info box: the approved-document location QR when present,
  // otherwise the existing plot QR (Edit documents).
  const qrLink = data.locationQrLink ?? data.qrCodeLink;

  // `data` (and `data.notes`, hence filteredNotes) is a fresh object/array
  // from the parent on every render, not just when the krookie being shown
  // actually changes — depending on those directly would re-run these
  // effects every render and, since some reset state and others can raise
  // it, ping-pong forever (React error #185 / blank screen, seen in
  // practice). Depend on primitives instead, which compare by value.
  const notesCount = filteredNotes.length;
  // Included in layoutKey below so editing notes (adding/removing one,
  // lengthening one) resets and re-measures density from scratch too —
  // without this, density only ever ratchets up (it has no mechanism to
  // notice it could now go back to a larger, more readable size) and
  // stays stuck at whatever it last escalated to for a *previous* note
  // count, shrinking the table more than the current notes actually need
  // and leaving unused space in the notes box.
  const notesSignature = filteredNotes.reduce(
    (total, note) => total + note.length,
    notesCount,
  );
  // The QR box (when present) adds height to the info box that the notes
  // box gives up in turn (see .qr's comment in the stylesheet) — it only
  // appears once a document is approved, after this layout was already
  // measured without it, so its arrival must also trigger a re-measure or
  // the stale, too-roomy density leaves the notes box overflowing under
  // the footer.
  const layoutKey = `${data.plotNumber}-${data.tableData.length}-${notesSignature}-${qrLink ? "qr" : "noqr"}`;

  // A row count alone can't predict whether a page fits — a moderate
  // table plus a long real note can still overflow one page even under
  // SPLIT_ROW_THRESHOLD, while a short table with no notes easily clears
  // it well above that count. The threshold is just a head start (skips
  // straight to two-page mode for an obviously long table, no need to
  // measure first); forcedSecondPage is the actual authority — set once
  // single-page mode has shrunk the table as far as it goes and still
  // doesn't fit (see the density effect below).
  const [forcedSecondPage, setForcedSecondPage] = useState(false);
  const needsSecondPage =
    data.tableData.length > SPLIT_ROW_THRESHOLD || forcedSecondPage;

  // --- Single-page mode: table shrinks to fit one page, same as before —
  // tried first even above SPLIT_ROW_THRESHOLD isn't needed here since
  // that already routes to two-page mode; this only runs for the "measure
  // first" case. ---
  const descriptionRef = useRef<HTMLDivElement>(null);
  const notesRef = useRef<HTMLDivElement>(null);
  const [tableDensity, setTableDensity] = useState<TableDensity>("normal");
  const [hideNoNotesText, setHideNoNotesText] = useState(false);
  const showNotesBox =
    filteredNotes.length > 0 || data.tableData.length <= SPLIT_ROW_THRESHOLD - 1;

  useLayoutEffect(() => {
    if (needsSecondPage) return;
    setTableDensity("normal");
    setForcedSecondPage(false);
  }, [layoutKey, needsSecondPage]);

  useLayoutEffect(() => {
    if (needsSecondPage) return;
    const el = descriptionRef.current;
    if (!el || el.scrollHeight <= el.clientHeight) return;
    if (tableDensity === "normal") setTableDensity("compact");
    else if (tableDensity === "compact") setTableDensity("dense");
    else if (tableDensity === "dense") setTableDensity("denser");
    // Already at the smallest step and it still doesn't fit — a real
    // note long enough to matter, most likely. Falling back to clipping
    // it (the old behaviour) loses actual written content; two-page mode
    // costs nothing.
    else setForcedSecondPage(true);
  }, [tableDensity, layoutKey, needsSecondPage]);

  useLayoutEffect(() => {
    if (needsSecondPage) return;
    const el = notesRef.current;
    if (!el || notesCount > 0) {
      setHideNoNotesText(false);
      return;
    }
    setHideNoNotesText(el.scrollHeight > el.clientHeight - 4);
  }, [tableDensity, notesCount, layoutKey, needsSecondPage]);

  // --- Two-page mode (> SPLIT_ROW_THRESHOLD rows): every row is kept, at
  // full size — page 1 gets as many as fit (header + plot info + maps +
  // table, no notes/footer), the rest spill to page 2 (table continued +
  // notes + footer + disclaimer). ---
  const page1CardRef = useRef<HTMLDivElement>(null);
  const [splitIndex, setSplitIndex] = useState(data.tableData.length);
  const page2BodyRef = useRef<HTMLDivElement>(null);
  const page2NotesRef = useRef<HTMLDivElement>(null);
  const [page2Density, setPage2Density] = useState<TableDensity>("normal");
  const [page2HideNoNotesText, setPage2HideNoNotesText] = useState(false);

  useLayoutEffect(() => {
    if (!needsSecondPage) return;
    // Try fitting everything on page 1 first; the next effect trims it
    // down to what actually fits.
    setSplitIndex(data.tableData.length);
  }, [needsSecondPage, layoutKey]);

  useLayoutEffect(() => {
    if (!needsSecondPage) return;
    const el = page1CardRef.current;
    if (!el) return;
    const overflow = el.scrollHeight - el.clientHeight;
    if (overflow <= 0) return;
    const rowsToRemove = Math.max(1, Math.ceil(overflow / TABLE_ROW_HEIGHT));
    setSplitIndex((prev) => Math.max(0, prev - rowsToRemove));
  }, [needsSecondPage, splitIndex, layoutKey]);

  const page2Rows = needsSecondPage ? data.tableData.slice(splitIndex) : [];

  useLayoutEffect(() => {
    if (!needsSecondPage) return;
    setPage2Density("normal");
  }, [needsSecondPage, layoutKey, splitIndex]);

  useLayoutEffect(() => {
    if (!needsSecondPage) return;
    const el = page2BodyRef.current;
    if (!el || el.scrollHeight <= el.clientHeight) return;
    if (page2Density === "normal") setPage2Density("compact");
    else if (page2Density === "compact") setPage2Density("dense");
    else if (page2Density === "dense") setPage2Density("denser");
  }, [needsSecondPage, page2Density, layoutKey, splitIndex]);

  useLayoutEffect(() => {
    if (!needsSecondPage) return;
    const el = page2NotesRef.current;
    if (!el || notesCount > 0) {
      setPage2HideNoNotesText(false);
      return;
    }
    setPage2HideNoNotesText(el.scrollHeight > el.clientHeight - 4);
  }, [needsSecondPage, page2Density, notesCount, layoutKey, splitIndex]);

  const header: ReactNode = (
    <div className={styles.header}>
      <div className={styles.section}>
        <h4>Sultanate of Oman</h4>
        <span>{data.cityEn.displayValue}</span>
        <p>Operations Department</p>
      </div>
      <img src={logo} alt="logo" />
      <div className={styles.section}>
        <h4>سلطنة عمان</h4>
        <span>{data.cityAr}</span>
        <p>دائرة العملیات</p>
      </div>
    </div>
  );

  const title: ReactNode = (
    <div className={styles.title}>
      <span className={styles.serial}>
        {t("card-krookie.serialNo")}: <span dir="ltr">{data.serial}</span>
      </span>
    </div>
  );

  const plotInfoBox: ReactNode = (
    <div className={styles.plotInfoBox}>
      <div className={styles.headerDesc}>
        <div className={styles.titleDesc}>
          <span>
            {t("card-krookie.plotNumber")}: {data.plotNumber}
          </span>
          <p>{t("card-krookie.plotDescription")}</p>
        </div>
        {qrLink && (
          <div className={styles.qr}>
            <QrCode href={qrLink} sizeQr={68} qrOnly={true} />
          </div>
        )}
      </div>
      <div className={styles.details}>
        <div className={styles.column}>
          <div className={styles.infoItem}>
            <span className={styles.label}>{t("card-krookie.streetNumber")}:</span>{" "}
            {data.streetNumber}
          </div>
          <div className={styles.infoItem}>
            <span className={styles.label}>{t("card-krookie.areaTable")}:</span>{" "}
            {Math.round(Number(data.area))}
          </div>
          <div className={styles.infoItem}>
            <span className={styles.label}>{t("card-krookie.height")}:</span>{" "}
            {data.height}
          </div>
        </div>

        <div className={styles.column}>
          <div className={styles.infoItem}>
            <span className={styles.label}>{t("card-krookie.phaseNumber")}:</span>{" "}
            {data.phase.displayValue?.split("-")[0]?.trim()}
          </div>
          <div className={styles.infoItem}>
            <span className={styles.label}>{t("card-krookie.landUse")}:</span>{" "}
            {data.landUse.displayValue}
          </div>
          <div className={styles.infoItem}>
            <span className={styles.label}>{t("card-krookie.buildArea")}:</span>{" "}
            {data.buildArea}
          </div>
        </div>
      </div>
      <div className={styles.setBack}>
        <span className={styles.label}>{t("card-krookie.setback")}:</span>
        <div className={styles.setBackValues}>
          <span className={styles.label}>
            {t("card-krookie.back")}:<p>{data.back}</p>
          </span>
          <span className={styles.label}>
            {t("card-krookie.side")}:<p>{data.side}</p>
          </span>
          <span className={styles.label}>
            {t("card-krookie.front")}:<p>{data.front}</p>
          </span>
        </div>
      </div>
    </div>
  );

  const screenshots: ReactNode = (
    <div className={styles.screenshots}>
      <div className={styles.screenshotWrapper}>
        <img
          className={styles.image}
          src={data.screenshots.firstScreenshot.url}
          alt="screenshot"
        />
        <p>
          {t("card-krookie.scale")}:{data.screenshots.firstScreenshot.scale}{" "}
          <img className={styles.compassImg} src={compass} alt="compass" />
        </p>
      </div>
      <div className={styles.screenshotWrapper}>
        <img
          className={styles.image}
          src={data.screenshots.secondScreenshot.url}
          alt="screenshot"
        />
        <p>
          {t("card-krookie.scale")}:
          {data.screenshots.secondScreenshot.scale}{" "}
          <img className={styles.compassImg} src={compass} alt="compass" />
        </p>
      </div>
    </div>
  );

  if (!needsSecondPage) {
    return (
      <div className={styles.card} dir="rtl" data-krookie-page="1">
        {header}
        {title}
        <div className={styles.info}>
          <div className={styles.description} ref={descriptionRef}>
            {plotInfoBox}
            <CoordinatesTable
              rows={data.tableData}
              plotNumber={data.plotNumber}
              wgs={data.wgs}
              area={data.area}
              density={tableDensity}
            />
            {showNotesBox && (
              <NotesBox
                filteredNotes={filteredNotes}
                hideNoNotesText={hideNoNotesText}
                notesRef={notesRef}
              />
            )}
          </div>
          {screenshots}
        </div>
        <FooterAndDisclaimer data={data} />
      </div>
    );
  }

  const page1Rows = data.tableData.slice(0, splitIndex);

  const page1: ReactNode = (
    <div
      className={styles.card}
      dir="rtl"
      data-krookie-page="1"
      ref={page1CardRef}
    >
      {header}
      {title}
      <div className={`${styles.info} ${styles.autoHeight}`}>
        <div className={`${styles.description} ${styles.autoHeight}`}>
          {plotInfoBox}
          <CoordinatesTable
            rows={page1Rows}
            plotNumber={data.plotNumber}
            wgs={data.wgs}
            area={data.area}
            density="normal"
          />
        </div>
        {screenshots}
      </div>
    </div>
  );

  // The editing preview only shows page 1 — page 2 (and so the document's
  // real, complete multi-page output) is only ever built from a separate,
  // undisplayed render of this component without previewOnly, right
  // before printing/saving (see document-krookie.tsx). Page 1's own
  // content is identical either way, since the row split is computed the
  // same way regardless.
  if (previewOnly) {
    return page1;
  }

  return (
    <div className={styles.pagesWrapper}>
      {page1}
      <div className={styles.card} dir="rtl" data-krookie-page="2">
        <div className={styles.page2Body} ref={page2BodyRef}>
          {page2Rows.length > 0 && (
            <CoordinatesTable
              rows={page2Rows}
              plotNumber={data.plotNumber}
              wgs={data.wgs}
              area={data.area}
              density={page2Density}
            />
          )}
          <NotesBox
            filteredNotes={filteredNotes}
            hideNoNotesText={page2HideNoNotesText}
            notesRef={page2NotesRef}
          />
        </div>
        <FooterAndDisclaimer data={data} />
      </div>
    </div>
  );
};

export default CardKrookie;
